import { useCallback, useEffect, useState } from 'react';
import { ContentPageHeader } from '@/components/global';
import { WarehouseTypeBadge } from '@/components/locations';
import { PasswordRequirementsChecklist } from '@/components/auth';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { apiFetch } from '@/lib/api';
import { notify } from '@/lib/notify';
import { useAuth } from '@/contexts/AuthContext';

interface WarehouseAssignment {
  warehouseId: string;
  warehouseCode: string;
  warehouseName: string;
  warehouseType: string;
}

interface ProfileUser {
  id: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  roles: string[];
  organizationName?: string;
  warehouseAssignments?: WarehouseAssignment[];
}

function isWarehouseType(value: string): value is 'HUB' | 'SPOKE' | 'VENUE' {
  return value === 'HUB' || value === 'SPOKE' || value === 'VENUE';
}

function parseProfileUser(data: unknown): ProfileUser | null {
  if (!data || typeof data !== 'object') return null;
  const root = data as { user?: unknown };
  const raw = (root.user && typeof root.user === 'object' ? root.user : data) as Record<
    string,
    unknown
  >;
  if (typeof raw.id !== 'string' || typeof raw.email !== 'string') return null;

  const roles = Array.isArray(raw.roles)
    ? raw.roles.filter((r): r is string => typeof r === 'string')
    : [];

  const warehouseAssignments = Array.isArray(raw.warehouseAssignments)
    ? raw.warehouseAssignments
        .filter((row): row is Record<string, unknown> => !!row && typeof row === 'object')
        .map((row) => ({
          warehouseId: typeof row.warehouseId === 'string' ? row.warehouseId : '',
          warehouseCode: typeof row.warehouseCode === 'string' ? row.warehouseCode : '',
          warehouseName: typeof row.warehouseName === 'string' ? row.warehouseName : '',
          warehouseType: typeof row.warehouseType === 'string' ? row.warehouseType : '',
        }))
        .filter((row) => row.warehouseId.length > 0)
    : [];

  return {
    id: raw.id,
    email: raw.email,
    firstName: typeof raw.firstName === 'string' ? raw.firstName : null,
    lastName: typeof raw.lastName === 'string' ? raw.lastName : null,
    roles,
    organizationName:
      typeof raw.organizationName === 'string' ? raw.organizationName : undefined,
    warehouseAssignments,
  };
}

const UNRESTRICTED_ROLES = new Set(['platformAdmin', 'tenantAdmin', 'admin', 'inventoryManager']);

export default function ProfilePage() {
  const { roles: authRoles } = useAuth();
  const [profile, setProfile] = useState<ProfileUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [savingPassword, setSavingPassword] = useState(false);

  const loadProfile = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiFetch('/api/auth/me');
      if (!res.ok) {
        setError('Failed to load profile');
        setProfile(null);
        return;
      }
      const data: unknown = await res.json();
      const parsed = parseProfileUser(data);
      if (!parsed) {
        setError('Invalid profile response');
        setProfile(null);
        return;
      }
      setProfile(parsed);
    } catch {
      setError('Failed to load profile');
      setProfile(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadProfile();
  }, [loadProfile]);

  const displayRoles = profile?.roles.length ? profile.roles : authRoles;
  const locationUnrestricted = displayRoles.some((r) => UNRESTRICTED_ROLES.has(r));
  const assignments = profile?.warehouseAssignments ?? [];
  const displayName =
    [profile?.firstName, profile?.lastName].filter(Boolean).join(' ') || '—';

  async function handleChangePassword(e: React.FormEvent) {
    e.preventDefault();
    if (!currentPassword || !newPassword) {
      notify.error('Enter your current and new password');
      return;
    }
    if (newPassword !== confirmPassword) {
      notify.error('New password and confirmation do not match');
      return;
    }

    setSavingPassword(true);
    try {
      const res = await apiFetch('/api/auth/change-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ currentPassword, newPassword }),
      });
      const data: unknown = await res.json().catch(() => null);
      if (!res.ok) {
        const message =
          data &&
          typeof data === 'object' &&
          'error' in data &&
          typeof (data as { error: unknown }).error === 'string'
            ? (data as { error: string }).error
            : data &&
                typeof data === 'object' &&
                'message' in data &&
                typeof (data as { message: unknown }).message === 'string'
              ? (data as { message: string }).message
              : 'Failed to change password';
        notify.error(message);
        return;
      }
      notify.success('Password updated');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch {
      notify.error('Failed to change password');
    } finally {
      setSavingPassword(false);
    }
  }

  return (
    <div className="space-y-6">
      <ContentPageHeader title="My Profile" />

      {loading ? (
        <p className="text-sm text-muted-foreground">Loading profile…</p>
      ) : error ? (
        <p className="text-sm text-destructive">{error}</p>
      ) : (
        <>
          <Card>
            <CardHeader>
              <CardTitle>Account</CardTitle>
              <CardDescription>Your account details (read-only).</CardDescription>
            </CardHeader>
            <CardContent className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-1">
                <p className="text-sm text-muted-foreground">Name</p>
                <p className="font-medium">{displayName}</p>
              </div>
              <div className="space-y-1">
                <p className="text-sm text-muted-foreground">Email</p>
                <p className="font-medium">{profile?.email ?? '—'}</p>
              </div>
              <div className="space-y-1">
                <p className="text-sm text-muted-foreground">Organization</p>
                <p className="font-medium">{profile?.organizationName ?? '—'}</p>
              </div>
              <div className="space-y-1">
                <p className="text-sm text-muted-foreground">Role</p>
                <div className="flex flex-wrap gap-1">
                  {displayRoles.length === 0 ? (
                    <span className="font-medium">—</span>
                  ) : (
                    displayRoles.map((role) => (
                      <Badge key={role} variant="secondary">
                        {role}
                      </Badge>
                    ))
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Assigned locations</CardTitle>
              <CardDescription>
                Locations you can access for inventory and operations.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {locationUnrestricted ? (
                <p className="text-sm text-muted-foreground">
                  All locations in this organization.
                </p>
              ) : assignments.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  No locations assigned. Ask an admin to assign hubs or spokes under Users.
                </p>
              ) : (
                <ul className="divide-y rounded-md border">
                  {assignments.map((loc) => (
                    <li
                      key={loc.warehouseId}
                      className="flex flex-wrap items-center justify-between gap-2 px-3 py-2"
                    >
                      <div className="min-w-0">
                        <p className="truncate font-medium">{loc.warehouseName}</p>
                        <p className="text-xs text-muted-foreground">{loc.warehouseCode}</p>
                      </div>
                      {isWarehouseType(loc.warehouseType) ? (
                        <WarehouseTypeBadge type={loc.warehouseType} size="sm" />
                      ) : (
                        <Badge variant="outline">{loc.warehouseType}</Badge>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Change password</CardTitle>
              <CardDescription>
                Choose a new password that meets the security requirements below.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form className="max-w-md space-y-4" onSubmit={handleChangePassword}>
                <div className="space-y-2">
                  <Label htmlFor="currentPassword">Current password</Label>
                  <Input
                    id="currentPassword"
                    type="password"
                    autoComplete="current-password"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="newPassword">New password</Label>
                  <Input
                    id="newPassword"
                    type="password"
                    autoComplete="new-password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                  />
                  <PasswordRequirementsChecklist password={newPassword} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">Confirm new password</Label>
                  <Input
                    id="confirmPassword"
                    type="password"
                    autoComplete="new-password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                  />
                </div>
                <Button type="submit" disabled={savingPassword}>
                  {savingPassword ? 'Updating…' : 'Update password'}
                </Button>
              </form>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
